import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useProviders } from '../../lib/features';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { Logo } from '../../components/Logo';
import { Button, Field, Input, PageLoader } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { toastError } from '../../lib/errors';
import { isLocale, useLocale, useT } from '../../lib/i18n';
import { queryClient, refreshSession } from '../../lib/queries';
import { useSession } from '../../lib/session';

function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-gradient-to-b from-brand-50 via-white to-white px-4 py-12">
      <Logo className="mb-8" />
      <div className="w-full max-w-sm rounded-2xl border border-zinc-200 bg-white p-7 shadow-xl shadow-zinc-900/5">
        <h1 className="text-xl font-semibold text-zinc-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-zinc-500">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
      {footer && <div className="mt-6 text-sm text-zinc-600">{footer}</div>}
      <LanguageSwitcher className="mt-6" />
    </div>
  );
}

/** "Continue with Google" + "or" divider, shown when the server has Google sign-in configured. */
function GoogleSignIn({ next }: { next: string | null }) {
  const t = useT();
  const locale = useLocale((s) => s.locale);
  const providers = useProviders();
  if (!providers.data?.google) return null;
  const qs = new URLSearchParams({ locale, ...(next && next.startsWith('/') ? { next } : {}) });
  return (
    <>
      <a
        href={`/api/auth/google/start?${qs}`}
        data-testid="google-signin"
        className="flex h-10 w-full items-center justify-center gap-2.5 rounded-lg border border-zinc-200 bg-white text-sm font-medium text-zinc-800 shadow-xs transition hover:bg-zinc-50"
      >
        <svg viewBox="0 0 48 48" className="size-[18px]" aria-hidden>
          <path
            fill="#FFC107"
            d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
          />
          <path
            fill="#FF3D00"
            d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
          />
          <path
            fill="#4CAF50"
            d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
          />
          <path
            fill="#1976D2"
            d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
          />
        </svg>
        {t('auth.google')}
      </a>
      <div className="my-5 flex items-center gap-3 text-xs text-zinc-400">
        <span className="h-px flex-1 bg-zinc-200" />
        {t('auth.or')}
        <span className="h-px flex-1 bg-zinc-200" />
      </div>
    </>
  );
}

/** Errors the Google callback sends back to the login page. */
function useGoogleError() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const code = params.get('error');
    if (!code?.startsWith('google_')) return;
    const message = params.get('message');
    const key = `auth.error.${code}` as const;
    toast.error(
      (['google_denied', 'google_state', 'google_failed', 'google_unavailable'].includes(code)
        ? t(key as 'auth.error.google_failed')
        : t('auth.error.google_failed')) + (message ? `: ${message}` : ''),
    );
    params.delete('error');
    params.delete('message');
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function useAfterLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  return async (workspaceId?: string) => {
    const me = await refreshSession();
    // The profile language applies unless this browser already picked one
    if (isLocale(me?.user?.locale) && !localStorage.getItem('bm.locale'))
      useLocale.getState().setLocale(me.user.locale);
    if (workspaceId) useSession.getState().setWorkspace(workspaceId);
    queryClient.clear();
    const next = params.get('next');
    navigate(next && next.startsWith('/') ? next : '/app/templates', { replace: true });
  };
}

export function LoginPage() {
  const t = useT();
  const after = useAfterLogin();
  const [params] = useSearchParams();
  useGoogleError();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/api/auth/login', { body: { email, password }, workspace: false });
      await after();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell
      title={t('auth.loginTitle')}
      subtitle={t('auth.loginSubtitle')}
      footer={
        <>
          {t('auth.noAccount')}{' '}
          <Link
            to={`/signup${params.get('next') ? `?next=${encodeURIComponent(params.get('next')!)}` : ''}`}
            className="font-medium text-brand-600 hover:text-brand-700"
          >
            {t('auth.signupLink')}
          </Link>
        </>
      }
    >
      <GoogleSignIn next={params.get('next')} />
      <form onSubmit={submit} className="space-y-4">
        <Field label={t('auth.email')}>
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            name="email"
          />
        </Field>
        <Field
          label={
            <span className="flex justify-between">
              {t('auth.password')}
              <Link
                to="/forgot-password"
                className="text-xs font-normal text-brand-600 hover:text-brand-700"
              >
                {t('auth.forgot')}
              </Link>
            </span>
          }
        >
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            name="password"
          />
        </Field>
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          {t('auth.loginButton')}
        </Button>
      </form>
    </AuthShell>
  );
}

const isRole = (r: string): r is 'owner' | 'admin' | 'editor' | 'viewer' =>
  ['owner', 'admin', 'editor', 'viewer'].includes(r);

export function SignupPage() {
  const t = useT();
  const locale = useLocale((s) => s.locale);
  const after = useAfterLogin();
  const [params] = useSearchParams();
  // Coming from an invitation: the invited address is already filled in
  const [form, setForm] = useState({
    name: '',
    email: params.get('email') ?? '',
    password: '',
    workspaceName: '',
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api<{ workspaceId: string }>('/api/auth/signup', {
        body: { ...form, locale },
        workspace: false,
      });
      await after(res.workspaceId);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell
      title={t('auth.signupTitle')}
      subtitle={t('auth.signupSubtitle')}
      footer={
        <>
          {t('auth.haveAccount')}{' '}
          <Link
            to={`/login${params.get('next') ? `?next=${encodeURIComponent(params.get('next')!)}` : ''}`}
            className="font-medium text-brand-600 hover:text-brand-700"
          >
            {t('auth.loginLink')}
          </Link>
        </>
      }
    >
      <GoogleSignIn next={params.get('next')} />
      <form onSubmit={submit} className="space-y-4">
        <Field label={t('auth.name')}>
          <Input
            autoComplete="name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            name="name"
          />
        </Field>
        <Field label={t('auth.email')}>
          <Input
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            name="email"
          />
        </Field>
        <Field label={t('auth.password')} hint={t('auth.passwordHint')}>
          <Input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            name="password"
          />
        </Field>
        <Field label={t('auth.company')} hint={t('auth.companyHint')}>
          <Input
            value={form.workspaceName}
            onChange={(e) => setForm({ ...form, workspaceName: e.target.value })}
            name="workspace"
          />
        </Field>
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          {t('auth.signupButton')}
        </Button>
        <p className="text-center text-xs text-zinc-400">{t('auth.freePlanNote')}</p>
        <p className="text-center text-xs text-zinc-500" data-testid="legal-consent">
          {t('legal.consentPrefix')}{' '}
          <Link to="/terms" target="_blank" className="underline hover:text-zinc-800">
            {t('legal.termsLower')}
          </Link>{' '}
          {t('legal.and')}{' '}
          <Link to="/privacy" target="_blank" className="underline hover:text-zinc-800">
            {t('legal.privacyLower')}
          </Link>
          .
        </p>
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const t = useT();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <AuthShell
      title={t('auth.forgotTitle')}
      subtitle={t('auth.forgotSubtitle')}
      footer={
        <Link to="/login" className="font-medium text-brand-600">
          ← {t('auth.loginLink')}
        </Link>
      }
    >
      {sent ? (
        <p className="text-sm text-zinc-600">{t('auth.forgotSent')}</p>
      ) : (
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              await api('/api/auth/forgot-password', { body: { email }, workspace: false });
              setSent(true);
            } catch (err) {
              toastError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field label={t('auth.email')}>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Button type="submit" variant="primary" className="w-full" loading={busy}>
            {t('auth.sendLink')}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <AuthShell title={t('auth.resetTitle')}>
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            await api('/api/auth/reset-password', {
              body: { token: params.get('token') ?? '', password },
              workspace: false,
            });
            toast.success(t('auth.resetDone'));
            navigate('/login');
          } catch (err) {
            toastError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label={t('auth.newPassword')} hint={t('auth.passwordHint')}>
          <Input
            type="password"
            minLength={8}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          {t('auth.resetButton')}
        </Button>
      </form>
    </AuthShell>
  );
}

export function InvitePage() {
  const t = useT();
  const { token } = useParams();
  const navigate = useNavigate();
  const user = useSession((s) => s.user);
  const [info, setInfo] = useState<{ email: string; role: string; workspaceName: string } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api(`/api/invitations/${token}`, { workspace: false }).then(setInfo, (err) =>
      setError(errorMessage(err)),
    );
  }, [token]);
  if (!info && !error) return <PageLoader />;
  const next = `/invite/${token}`;
  return (
    <AuthShell title={t('invite.title')}>
      {error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-zinc-600">
            {t('invite.text', {
              workspace: info!.workspaceName,
              role: isRole(info!.role) ? t(`role.${info!.role}`).toLowerCase() : info!.role,
            })}
          </p>
          {user ? (
            <Button
              variant="primary"
              className="w-full"
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const res = await api<{ workspaceId: string }>(
                    `/api/invitations/${token}/accept`,
                    { method: 'POST', workspace: false },
                  );
                  await refreshSession();
                  useSession.getState().setWorkspace(res.workspaceId);
                  queryClient.clear();
                  navigate('/app/templates');
                } catch (err) {
                  toastError(err);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t('invite.accept')}
            </Button>
          ) : (
            <div className="grid gap-2">
              <Link
                to={`/signup?next=${encodeURIComponent(next)}&email=${encodeURIComponent(info!.email)}`}
                className="block"
              >
                {/* Long addresses wrap inside the card instead of pushing the button out */}
                <Button
                  variant="primary"
                  className="h-auto! min-h-10 w-full py-2! text-center whitespace-normal! [overflow-wrap:anywhere]"
                >
                  {t('invite.signup', { email: info!.email })}
                </Button>
              </Link>
              <Link
                to={`/login?next=${encodeURIComponent(next)}&email=${encodeURIComponent(info!.email)}`}
                className="block"
              >
                <Button className="w-full">{t('auth.loginButton')}</Button>
              </Link>
            </div>
          )}
        </div>
      )}
    </AuthShell>
  );
}

/** Shown to a signed-in user who belongs to no workspace (e.g. removed from their only team). */
export function NoWorkspacePage() {
  const t = useT();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const ws = await api<{ id: string }>('/api/workspaces', { body: { name } });
      await refreshSession();
      useSession.getState().setWorkspace(ws.id);
      queryClient.clear();
      navigate('/app/templates');
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell
      title={t('workspace.noneTitle')}
      subtitle={t('workspace.noneText')}
      footer={
        <button
          className="font-medium text-brand-600 hover:underline"
          onClick={async () => {
            await api('/api/auth/logout', { method: 'POST', workspace: false });
            useSession.getState().setSession(null, []);
            queryClient.clear();
            navigate('/login');
          }}
        >
          {t('nav.logout')}
        </button>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label={t('auth.company')}>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Marketing"
            required
            autoFocus
          />
        </Field>
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          {t('nav.newWorkspace')}
        </Button>
      </form>
    </AuthShell>
  );
}
